import { fieldIdentity } from "@/lib/itsm-config/field-mapping";
import type {
  JiraFieldMapping,
  JiraHarmonyField,
  MondaySyncDirection,
  ProviderConfig,
} from "@/lib/types/itsm-config";

/** Built-ins a Jira mapping may name. Status, priority, assignee and
 *  reporter already sync both ways on their own Jira fields. */
export const JIRA_BUILTIN_FIELDS = ["title", "description"] as const;

/** The Jira field each text built-in fills when the issue is created;
 *  mapping a built-in onto it keeps that field in step afterwards. */
export const JIRA_BUILTIN_HOME_FIELDS: Record<string, string> = {
  title: "summary",
  description: "description",
};

/** Jira fields Harmony already writes from its own field, or that hold no
 *  value. Mirrors the backend's reserved targets. */
export const JIRA_RESERVED_TARGETS = new Set([
  "project",
  "issuetype",
  "priority",
  "assignee",
  "reporter",
  "status",
  "attachment",
  "comment",
  "issuelinks",
  "parent",
  "subtasks",
  "worklog",
  "watches",
  "votes",
]);

/** Jira field types a Harmony value can be written to. */
export const JIRA_WRITABLE_FIELD_TYPES = new Set([
  "string",
  "number",
  "option",
  "date",
  "datetime",
  "any",
  "array:option",
  "array:string",
  "array:component",
  "array:version",
]);

/** How a pairing syncs: a direction, or only when the issue is created. */
export type JiraPairingSync = MondaySyncDirection | "create";

export interface JiraPairing {
  harmony: JiraHarmonyField;
  target: string;
  sync: JiraPairingSync;
  /** Index into `field_mappings`. */
  index: number;
  /** Displaced by an earlier entry or the Harmony link field: never syncs,
   *  and the backend refuses to save the config while it is there. */
  conflict: boolean;
}

/** A mapping's sync, as the editor shows it. */
export function pairingSync(mapping: JiraFieldMapping): JiraPairingSync {
  if (mapping.create_only) return "create";
  return mapping.direction ?? "both";
}

/** The mapping entry for a sync chosen in the editor. */
export function withSync(
  mapping: JiraFieldMapping,
  sync: JiraPairingSync,
): JiraFieldMapping {
  if (sync === "create") {
    return { ...mapping, direction: "outbound", create_only: true };
  }
  return { ...mapping, direction: sync, create_only: null };
}

/**
 * Every entry, as the backend reads them
 * (`itsm_integration_core.jira_field_mapping`): in order, the first claim of a
 * Harmony field or a Jira field winning. The Harmony link field is never a
 * target. A displaced entry is kept, marked as a conflict, so it can be
 * removed.
 */
export function jiraPairings(config: ProviderConfig): JiraPairing[] {
  const usedFields = new Set<string>();
  const usedTargets = new Set<string>(
    config.harmony_link_field ? [config.harmony_link_field] : [],
  );
  const pairings: JiraPairing[] = [];
  (config.field_mappings ?? []).forEach((m, index) => {
    const id = fieldIdentity(m.harmony);
    const conflict = usedFields.has(id) || usedTargets.has(m.target);
    if (!conflict) {
      usedFields.add(id);
      usedTargets.add(m.target);
    }
    pairings.push({
      harmony: m.harmony,
      target: m.target,
      sync: pairingSync(m),
      index,
      conflict,
    });
  });
  return pairings;
}

/** Fields and targets a new entry may not use. */
export function jiraClaimed(config: ProviderConfig): {
  fields: Set<string>;
  targets: Set<string>;
} {
  const fields = new Set<string>();
  const targets = new Set<string>(
    config.harmony_link_field ? [config.harmony_link_field] : [],
  );
  for (const m of config.field_mappings ?? []) {
    fields.add(fieldIdentity(m.harmony));
    targets.add(m.target);
  }
  return { fields, targets };
}

/** Whether a Jira field can take this Harmony field, by the backend's rules. */
export function isAllowedTarget(
  harmony: JiraHarmonyField | null,
  target: string,
): boolean {
  if (JIRA_RESERVED_TARGETS.has(target)) return false;
  const homes = Object.values(JIRA_BUILTIN_HOME_FIELDS);
  if (!homes.includes(target)) return true;
  return (
    harmony?.kind === "builtin" &&
    JIRA_BUILTIN_HOME_FIELDS[harmony.key] === target
  );
}

/**
 * The syncs a pairing may take. Built-ins only ever write out, and one on its
 * own Jira field is filled at creation anyway - "at creation only" there
 * would never write anything.
 */
export function allowedSyncs(
  harmony: JiraHarmonyField | null,
  target?: string,
): JiraPairingSync[] {
  if (harmony?.kind !== "builtin") {
    return ["both", "outbound", "inbound", "create"];
  }
  const home = JIRA_BUILTIN_HOME_FIELDS[harmony.key];
  return target && target === home ? ["outbound"] : ["outbound", "create"];
}

/** The draft with the field mappings replaced; empty saves as null. */
export function fieldMappingsPatch(
  mappings: JiraFieldMapping[],
): Pick<ProviderConfig, "field_mappings"> {
  return { field_mappings: mappings.length ? mappings : null };
}
